import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { ReadyState } from "react-use-websocket";
import Modal from "../Modal";
import { Dialog } from "@headlessui/react";
import Logo from "../../assets/Logo.png";
import api from "../../api";

const isoToWsCreatedAt = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, "0");
    return `${pad(d.getUTCDate())}-${pad(d.getUTCMonth() + 1)}-${d.getUTCFullYear()} ${pad(
        d.getUTCHours()
    )}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}`;
};

const APPOINTMENT_STATUS_LABELS = {
    1: "Pendiente",
    2: "Recordatorio enviado",
    3: "Confirmado",
    4: "Cancelado",
    5: "Recibido",
};

const timeToMinutes = (t) => {
    if (!t) return Infinity;
    const [h, m] = t.split(":").map(Number);
    return h * 60 + m;
};
const byScheduledTime = (a, b) =>
    timeToMinutes(a.sort_time ?? a.scheduled_time) -
    timeToMinutes(b.sort_time ?? b.scheduled_time);

const CallerList = ({
    sendJsonMessage,
    userData,
    lastJsonMessage,
    selectedMedicalOffice,
    setStep,
    consultationType,
    readyState,
}) => {
    let didInit = false;
    const [queue, setQueue] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [turnToSkip, setTurnToSkip] = useState(null);
    const [lastSyncTime, setLastSyncTime] = useState(new Date());

    const getPatientQueue = () => {
        sendJsonMessage(
            {
                action: "get_queue",
                data: {
                    doctor_id: userData.id,
                },
            },
            false
        );
    };

    const callPatient = (patientId) => {
        sendJsonMessage(
            {
                action: "call_patient",
                data: {
                    doctor_id: userData.id,
                    medical_office_id: selectedMedicalOffice,
                    patient_id: patientId,
                },
            },
            false
        );
    };

    const skipPatient = () => {
        sendJsonMessage(
            {
                action: "mark_missed_appointment",
                data: {
                    turn_id: turnToSkip.id,
                },
            },
            false
        );
        setOpen(false);
    };

    const removeFromQueue = (turnId) => {
        setQueue(queue.filter((row) => row.id !== turnId));
    };

    const finishTurn = (turnId) => {
        sendJsonMessage(
            {
                action: "mark_attended",
                data: { turn_id: turnId },
            },
            false
        );

        setQueue(
            queue.map((row) =>
                row.id === turnId ? { ...row, attended: true } : row
            )
        );
    };


    useEffect(() => {
        if (!didInit) {
            didInit = true;
            getPatientQueue();
        }

        return () => {
            sendJsonMessage(
                {
                    action: "logout",
                    data: {
                        doctor_id: userData.id,
                    },
                },
                false
            );
        };
    }, []);

    useEffect(() => {
        if (readyState === ReadyState.OPEN) return;

        let cancelled = false;
        const fetchViaRest = async () => {
            try {
                const today = new Date().toISOString().slice(0, 10);
                const [turns, appointments] = await Promise.all([
                    api.turns.getMulti(null, null, null, null, {
                        doctor: userData.id,
                        date: today,
                    }),
                    api.appointments.getMulti(null, null, null, null, {
                        doctor: userData.id,
                        date: today,
                    }),
                ]);
                if (cancelled) return;

                const idsConTurno = new Set(
                    turns.map((t) => t.appointment).filter(Boolean)
                );
                const arrivedRows = turns.map((t) => ({
                    id: t.id,
                    patient: `${t.patient?.firstName ?? ""} ${
                        t.patient?.lastName ?? ""
                    }`.trim(),
                    patient_id: t.patient?.id,
                    gov_id: t.patient?.govId,
                    created_at: isoToWsCreatedAt(t.createdAt),
                    scheduled_time: t.scheduledTime,
                    sort_time: t.scheduledTime ?? t.createdAt?.slice(11, 16),
                    appointment_id: t.appointment,
                    appointment_status: t.appointmentStatus,
                    appointment_notes: t.appointmentNotes,
                    attended: t.attended,
                    is_urgent: t.isUrgent,
                    last_diagnosis: null,
                    arrived: true,
                    ready: false,
                    on_hold: false,
                }));

                const pendingRows = appointments
                    .filter(
                        (a) =>
                            [1, 2, 3].includes(a.status) &&
                            !idsConTurno.has(a.id)
                    )
                    .map((a) => ({
                        id: null,
                        appointment_id: a.id,
                        patient: `${a.patient?.firstName ?? ""} ${
                            a.patient?.lastName ?? ""
                        }`.trim(),
                        patient_id: a.patient?.id,
                        gov_id: a.patient?.govId,
                        age: a.patient?.age,
                        scheduled_time: a.time,
                        appointment_status:
                            APPOINTMENT_STATUS_LABELS[a.status] ?? null,
                        appointment_notes: a.notes,
                        arrived: false,
                        ready: false,
                        on_hold: false,
                    }));

                // el WS ya manda "queue" pre-ordenada; acá armamos el fallback a mano
                setQueue([...arrivedRows, ...pendingRows].sort(byScheduledTime));

                setLoading(false);
            } catch (e) {
                console.error("Fallback REST de cola falló:", e);
            }
        };

        fetchViaRest();
        const interval = setInterval(fetchViaRest, 10000);
        return () => {
            cancelled = true;
            clearInterval(interval);
        };
    }, [readyState, userData.id]);

    useEffect(() => {
        if (!lastJsonMessage) return;

        console.log("WebSocket mensaje recibido:", lastJsonMessage);

        if (
            lastJsonMessage.type === "no_patient" &&
            lastJsonMessage.status === "pass"
        ) {
            setStep(3);
        } else if (
            lastJsonMessage.type === "connection" &&
            lastJsonMessage.status === "success" &&
            lastJsonMessage.message?.includes("Conectado a la cola")
        ) {
            // Cuando se reconecta al grupo, la cola ya fue enviada automáticamente por el backend
            console.log("Reconectado al grupo, esperando cola actualizada...");
        } else if (
            lastJsonMessage.action === "create_turn" ||
            (lastJsonMessage.type === "queue" &&
                lastJsonMessage.status === "success" &&
                lastJsonMessage.message === "Nuevo turno creado.")
        ) {

            const newTurn = lastJsonMessage.data?.turn ?? lastJsonMessage.data;
            if (!newTurn) return;

            setQueue((prevQueue) => {
                const idx = prevQueue.findIndex(
                    (row) =>
                        row.id === newTurn.id ||
                        (newTurn.appointment_id &&
                            row.appointment_id === newTurn.appointment_id &&
                            !row.arrived)
                );
                const updatedRow = {
                    ...newTurn,
                    arrived: true,
                    ready: false,
                    on_hold: false,
                };
                if (idx !== -1) {
                    const next = [...prevQueue];
                    next[idx] = { ...prevQueue[idx], ...updatedRow };
                    return next;
                }
                return [...prevQueue, updatedRow].sort(byScheduledTime);
            });
        } else if (
            lastJsonMessage.type === "queue" &&
            lastJsonMessage.status === "success" &&
            lastJsonMessage.data?.queue
        ) {
            console.log(
                "Cola sincronizada, filas recibidas:",
                lastJsonMessage.data.queue.length
            );

            setQueue(
                lastJsonMessage.data.queue.map((row) => ({
                    ...row,
                    ready: false,
                    on_hold: false,
                }))
            );
            setLastSyncTime(new Date());
            setLoading(false);
        } else if (
            lastJsonMessage.type === "appointment" &&
            lastJsonMessage.status === "success"
        ) {
            // Nueva cita creada
            if (lastJsonMessage.message === "Nuevo appointment creado" && lastJsonMessage.data) {
                const newAppointment = {
                    id: null,
                    appointment_id: lastJsonMessage.data.id,
                    patient_id: lastJsonMessage.data.patient?.id,
                    patient: lastJsonMessage.data.patient
                        ? `${lastJsonMessage.data.patient.firstName} ${lastJsonMessage.data.patient.lastName}`
                        : null,
                    gov_id: lastJsonMessage.data.patient?.gov_id,
                    age: lastJsonMessage.data.patient?.age,
                    scheduled_time: lastJsonMessage.data.time ? lastJsonMessage.data.time.slice(0, 5) : null,
                    appointment_notes: lastJsonMessage.data.notes,
                    appointment_status: lastJsonMessage.data.status,
                    arrived: false,
                    ready: false,
                    on_hold: false,
                };

                // Solo agregar si el paciente existe y no está ya en la cola
                if (newAppointment.patient) {
                    setQueue((prevQueue) => {
                        const alreadyThere = prevQueue.some(
                            (row) => row.appointment_id === newAppointment.appointment_id
                        );
                        if (alreadyThere) return prevQueue;
                        return [...prevQueue, newAppointment].sort(byScheduledTime);
                    });
                }
            }
            setLoading(false);
        }
    }, [lastJsonMessage]);

    const confirmation = (
        <div className="bg-white dark:bg-gray-900 text-black dark:text-white">
            <div>
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100">
                    <ExclamationTriangleIcon
                        className="h-6 w-6 text-yellow-800"
                        aria-hidden="true"
                    />
                </div>
                <div className="mt-3 text-center sm:mt-5">
                    <Dialog.Title
                        as="h3"
                        className="text-lg leading-6 font-medium text-black dark:text-white"
                    >
                        Confirmar
                    </Dialog.Title>
                    <div className="mt-2">
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                            ¿Estás seguro de que quieres marcar como ausente a
                            este paciente?
                        </p>
                    </div>
                </div>
            </div>
            <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                <button
                    type="button"
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary text-base font-medium text-white hover:bg-primary sm:col-start-2 sm:text-sm"
                    onClick={() => {
                        skipPatient();
                        removeFromQueue(turnToSkip.id);
                    }}
                >
                    Aceptar
                </button>
                <button
                    type="button"
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 sm:mt-0 sm:col-start-1 sm:text-sm"
                    onClick={() => {
                        setTurnToSkip(null);
                        setOpen(false);
                    }}
                >
                    Cancelar
                </button>
            </div>
        </div>
    );

    return (
        <div className="-my-2 sm:-mx-6 lg:-mx-8 w-full self-start mt-8">
            {loading ? (
                <div className="h-screen flex items-center justify-center">
                    <div className="w-10 h-10 mx-5 border-b-2 border-primary rounded-full animate-spin"></div>
                </div>
            ) : (
                <div>
                    {queue.some((row) => row.arrived) ? (
                        <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                            <h1 class="text-2xl font-bold leading-9 text-gray-600 dark:text-gray-400 mb-8">
                                Gestión de turnos de consultas -{" "}
                                {`${userData.gender === 2 ? "Dra" : "Dr"}. ${
                                    userData.firstName
                                } ${userData.lastName}`}
                            </h1>

                            <div
                                className={` border-gray-200 dark:border-gray-700`}
                            >
                                <Modal
                                    size="sm:max-w-lg w-full"
                                    content={confirmation}
                                    open={open}
                                    setOpen={setOpen}
                                />

                                <div className="max-w-[95dvw]">
                                    <h2 className="text-gray-600 dark:text-gray-400 text-xl font-bold mb-8">
                                        Agenda y llegadas del día
                                    </h2>
                                    <CombinedTable
                                        rows={queue}
                                        callPatient={callPatient}
                                        setQueue={setQueue}
                                        setTurnToSkip={setTurnToSkip}
                                        finishTurn={finishTurn}
                                        setOpen={setOpen}
                                    />
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="flex flex-col md:flex-row gap-40">
                            <div className="max-w-[95dvw]">
                                <h2 className="text-gray-600 dark:text-gray-400 text-xl font-bold mb-8">
                                    Mi agenda del día
                                </h2>
                                <CombinedTable
                                    rows={queue}
                                    callPatient={callPatient}
                                    setQueue={setQueue}
                                    setTurnToSkip={setTurnToSkip}
                                    finishTurn={finishTurn}
                                    setOpen={setOpen}
                                />
                            </div>
                            <div className=" sm:w-full sm:max-w-md">
                                <img
                                    className="w-48 mx-auto"
                                    src={Logo}
                                    alt="Logo"
                                />
                                <h2 className="text-center text-[30px] font-bold leading-9 text-primary mb-10">
                                    Ya no quedan pacientes por atender
                                </h2>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};
export default CallerList;

const CombinedTable = ({
    rows,
    callPatient,
    setQueue,
    setTurnToSkip,
    finishTurn,
    setOpen,
}) => {
    return (
        <div className="w-full overflow-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 border border-gray-200 dark:border-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-900 z-30 shadow-sm">
                    <tr>
                        <th
                            scope="col"
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                        >
                            Paciente
                        </th>
                        <th
                            scope="col"
                            className="py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                        >
                            Hora Cita
                        </th>
                        <th
                            scope="col"
                            className="py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                        >
                            Hora Llegada
                        </th>
                        <th
                            scope="col"
                            className=" py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                        >
                            Edad
                        </th>
                        {/* <th
                            scope="col"
                            className="py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                        >
                            CI
                        </th> */}
                        <th
                            scope="col"
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                        >
                            Notas
                        </th>
                        <th
                            scope="col"
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                        >
                            Dx
                        </th>
                        <th
                            scope="col"
                            className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                        >
                            Estado
                        </th>
                        <th
                            scope="col"
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                        ></th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
                    {rows.length === 0 ? (
                        <tr>
                            <td colSpan={9} className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">
                                No hay pacientes ni citas pendientes
                            </td>
                        </tr>
                    ) : (
                        rows.map((el) => (
                        <tr
                            key={el.id ?? `cita-${el.appointment_id}`}
                            className={
                                el.attended
                                    ? "bg-[#9CEC5B] dark:bg-[#255507]"
                                    : el.on_hold
                                    ? "bg-[#ffd6a8]"
                                    : ""
                            }
                        >
                            <td className="px-6 whitespace-nowrap text-left text-sm font-medium text-primary hover:text-primary">
                                <Link
                                    to={`/patients/${el.patient_id}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {el.patient}
                                </Link>
                            </td>
                            <td className="whitespace-nowrap text-center text-sm font-medium">
                                <div className="text-gray-900 dark:text-white">
                                    {el.scheduled_time || "--:--"}
                                </div>
                            </td>
                            <td className="whitespace-nowrap text-center text-sm font-medium">
                                <div className="text-gray-900 dark:text-white">
                                    {el.arrived && el.created_at ? (() => {
                                        const [_, time] = el.created_at.split(" ");
                                        let [hours, minutes] = time.split(":").map(Number);
                                        hours = (hours - 3 + 24) % 24;
                                        return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
                                    })() : "--:--"}
                                </div>
                            </td>
                            <td className="whitespace-nowrap text-center text-sm font-medium">
                                <div className="text-gray-900 dark:text-white">
                                    {el.age < 1 ? "RN" : el.age || "--"}
                                </div>
                            </td>
                            {/* <td className="whitespace-nowrap text-right text-sm font-medium">
                                <div className="text-gray-900 dark:text-white">
                                    {el.gov_id || "--"}
                                </div>
                            </td> */}
                            <NotesCell notes={el.appointment_notes || (el.is_urgent ? "Urgente" : null)} />
                            <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-200 max-w-xs">
                                {el.last_diagnosis || "--"}
                            </td>
                            <td className="px-6 whitespace-nowrap text-center text-sm font-medium">
                                {!el.arrived ? (
                                    <span className="inline-flex items-center rounded-md bg-yellow-50 px-2 py-1 text-xs font-medium text-yellow-700 ring-1 ring-inset ring-yellow-600/10">
                                        Pendiente
                                    </span>
                                ) : el.attended ? (
                                    <span className="inline-flex items-center rounded-md bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/10">
                                        Atendido
                                    </span>
                                ) : (
                                    <span className="inline-flex items-center rounded-md bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-600/10">
                                        Llegó
                                    </span>
                                )}
                            </td>

                            <td className="whitespace-nowrap px-3 py-3 text-sm text-gray-500">
                                {el.arrived && !el.attended ? (
                                    <div className="flex gap-x-2 justify-center">
                                        <button
                                            onClick={() =>
                                                callPatient(el.patient_id)
                                            }
                                            className="w-1/3  flex justify-center py-2 px-4 border border-[#75C9EE] rounded-md shadow-sm text-sm font-medium text-[#75C9EE] active:bg-blue-100"
                                        >
                                            Llamar
                                        </button>
                                        <button
                                            onClick={() =>
                                                setQueue((prevQueue) =>
                                                    prevQueue.map((row) =>
                                                        row.gov_id === el.gov_id
                                                            ? { ...row, on_hold: true }
                                                            : row
                                                    )
                                                )
                                            }
                                            className="w-1/3 flex justify-center py-2 px-4 border rounded-md shadow-sm text-sm font-medium text-amber-600 border-amber-400 active:bg-amber-100"
                                        >
                                            En espera
                                        </button>
                                        <button
                                            onClick={() => {
                                                setTurnToSkip(el);
                                                setOpen(true);
                                            }}
                                            className="w-1/3  flex justify-center py-2 px-4 border rounded-md shadow-sm text-sm font-medium text-white  bg-red active:bg-rose-400"
                                        >
                                            Ausente
                                        </button>
                                        <button
                                            onClick={() => finishTurn(el.id)}
                                            className="w-1/3  flex justify-center py-2 px-4 border rounded-md shadow-sm text-sm font-medium text-teal-600  border-teal-600 active:bg-teal-100"
                                        >
                                            Finalizar
                                        </button>
                                    </div>
                                ) : null}
                            </td>
                        </tr>
                    ))
                    )}
                </tbody>
            </table>
        </div>
    );
};

function AppointmentsTable({ appointments }) {
    const formatTime = (timeString) => {
        return timeString ? timeString.slice(0, 5) : "--:--";
    };

    return (
        <div className=" w-full overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 border border-gray-200 dark:border-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-900">
                    <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Paciente
                        </th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Hora
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Notas
                        </th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
                    {appointments.length === 0 ? (
                        <tr>
                            <td
                                colSpan={3}
                                className="px-6 py-4 text-center text-gray-500 dark:text-gray-400"
                            >
                                No hay citas disponibles
                            </td>
                        </tr>
                    ) : (
                        appointments.map((appt) => (
                            <tr
                                key={appt.id}
                                className="hover:bg-gray-100 dark:hover:bg-gray-700"
                            >
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium ">
                                    {appt.patient ? (
                                        <Link
                                            to={`/patients/${appt.patient.id}`}
                                            className="hover:underline text-primary hover:text-primary"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            {appt.patient.firstName}{" "}
                                            {appt.patient.lastName}
                                        </Link>
                                    ) : (
                                        <span className="text-red-800 bg-red-100 font-semibold p-1 rounded-full">
                                            Bloqueado
                                        </span>
                                    )}
                                </td>

                                <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-900 dark:text-white">
                                    {formatTime(appt.time)}
                                </td>

                                <NotesCell notes={appt.notes} />
                            </tr>
                        ))
                    )}
                </tbody>
            </table>
        </div>
    );
}

function NotesCell({ notes }) {
    const [expanded, setExpanded] = useState(false);

    if (!notes)
        return (
            <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-200">
                --
            </td>
        );

    const cleanText = notes.replace(/<[^>]+>/g, "");
    const limit = 100;
    const shouldTruncate = cleanText.length > limit;

    return (
        <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-200 max-w-xs">
            <div>
                <div
                    className={`${
                        !expanded && "line-clamp-3 overflow-hidden"
                    } `}
                    dangerouslySetInnerHTML={{
                        __html: expanded
                            ? notes
                            : cleanText.slice(0, limit) +
                              (shouldTruncate ? "..." : ""),
                    }}
                ></div>
                {shouldTruncate && (
                    <button
                        className="text-primary hover:underline mt-1 text-xs"
                        onClick={() => setExpanded(!expanded)}
                    >
                        {expanded ? "Ver menos" : "Ver más"}
                    </button>
                )}
            </div>
        </td>
    );
}
